/**
 * Platform Video Service - Customer Connects API Platform
 * 
 * This service bridges the existing RealtimeKit integration with the new
 * customer API platform, providing namespace isolation and usage tracking.
 */

// Configuration for RealtimeKit API
export const REALTIMEKIT_CONFIG = {
  organizationId: '543dbec3-e3c7-4b7f-a511-b2a954bfecfa',
  apiKey: 'b4714c9ff0e16a6223c7',
  authHeader: 'Basic NTQzZGJlYzMtZTNjNy00YjdmLWE1MTEtYjJhOTU0YmZlY2ZhOmI0NzE0YzlmZjBlMTZhNjIyM2M3',
  baseUrl: 'https://rtk.realtime.cloudflare.com/v2'
};

// Platform API types
export interface PlatformRoom {
  roomId: string;
  name: string;
  namespacedName: string;
  maxParticipants: number;
  status: 'waiting' | 'active' | 'ended';
  createdAt: string;
  expiresAt?: string;
  metadata?: {
    customerId: string;
    organizationId: string;
    originalName: string;
    purpose?: string;
  };
}

export interface PlatformToken {
  token: string;
  roomId: string;
  participantId: string;
  participantName: string;
  role: 'host' | 'participant' | 'viewer';
  expiresAt: string;
}

export interface CreateRoomRequest {
  name: string;
  maxParticipants?: number;
  recordingEnabled?: boolean;
  expiresIn?: number; // seconds
  metadata?: Record<string, any>;
}

export interface CreateTokenRequest {
  participantName: string;
  role: 'host' | 'participant' | 'viewer';
  permissions?: {
    canPublishVideo?: boolean;
    canPublishAudio?: boolean;
    canScreenShare?: boolean;
    canRecord?: boolean;
  };
  metadata?: Record<string, any>;
}

export interface RoomStatus {
  roomId: string;
  name: string;
  status: string;
  participants: {
    current: number;
    max: number;
    list: Array<{
      participantId: string;
      name: string;
      role: string;
      joinedAt: string;
      status: string;
    }>;
  };
  duration: number;
  recording?: {
    enabled: boolean;
    status: string;
    duration: number;
  };
}

export class PlatformVideoService {
  private readonly PLATFORM_PREFIX = 'cc_platform'; // Customer Connects Platform
  private headers: HeadersInit;

  constructor() {
    this.headers = {
      'Authorization': REALTIMEKIT_CONFIG.authHeader,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Create a namespaced room name for customer isolation
   */
  private createNamespacedRoomName(organizationId: string, roomName: string): string {
    const sanitizedName = roomName.replace(/[^a-zA-Z0-9_-]/g, '_').toLowerCase();
    return `${this.PLATFORM_PREFIX}_${organizationId}_${sanitizedName}_${Date.now()}`;
  }

  /**
   * Create a room for a platform customer (API endpoint implementation)
   */
  async createRoom(
    organizationId: string,
    request: CreateRoomRequest
  ): Promise<PlatformRoom> {
    const namespacedName = this.createNamespacedRoomName(organizationId, request.name);
    
    console.log('[PlatformVideo] Creating room:', namespacedName);
    
    try {
      const response = await fetch(`${REALTIMEKIT_CONFIG.baseUrl}/meetings`, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify({
          title: namespacedName,
          preferred_region: 'us-east-1',
          max_participants: request.maxParticipants || 10,
          metadata: {
            platform: 'customer-connects',
            organizationId,
            originalName: request.name,
            purpose: request.metadata?.purpose || 'general',
            recordingEnabled: request.recordingEnabled || false,
            createdAt: new Date().toISOString(),
            ...request.metadata
          }
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create platform room: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      
      const room: PlatformRoom = {
        roomId: data.data.id,
        name: request.name,
        namespacedName,
        maxParticipants: request.maxParticipants || 10,
        status: 'waiting',
        createdAt: data.data.created_at || new Date().toISOString(),
        expiresAt: request.expiresIn 
          ? new Date(Date.now() + request.expiresIn * 1000).toISOString()
          : undefined,
        metadata: {
          customerId: organizationId, // For backward compatibility
          organizationId,
          originalName: request.name,
          purpose: request.metadata?.purpose
        }
      };

      // TODO: Log usage to D1 database
      await this.logRoomCreation(organizationId, room);

      return room;
    } catch (error) {
      console.error('[PlatformVideo] Error creating room:', error);
      throw error;
    }
  }

  /**
   * Generate join token for a room
   */
  async createToken(
    roomId: string,
    organizationId: string,
    request: CreateTokenRequest
  ): Promise<PlatformToken> {
    try {
      // Determine preset based on role and permissions
      let presetName = 'group_call_participant';
      if (request.role === 'host') {
        presetName = 'group_call_host';
      } else if (request.role === 'viewer') {
        presetName = 'group_call_viewer';
      }
      
      const customParticipantId = `${organizationId}_${request.role}_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
      
      const response = await fetch(`${REALTIMEKIT_CONFIG.baseUrl}/meetings/${roomId}/participants`, {
        method: 'POST',
        headers: this.headers,
        body: JSON.stringify({
          name: request.participantName,
          preset_name: presetName,
          custom_participant_id: customParticipantId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to generate token: ${response.status} ${errorText}`);
      }

      const data = await response.json();
      
      const token: PlatformToken = {
        token: data.data.token,
        roomId,
        participantId: data.data.participant_id,
        participantName: request.participantName,
        role: request.role,
        expiresAt: new Date(Date.now() + 60 * 60 * 1000).toISOString() // 1 hour
      };

      // TODO: Log token generation for billing
      await this.logTokenGeneration(organizationId, roomId, token);

      return token;
    } catch (error) {
      console.error('[PlatformVideo] Error generating token:', error);
      throw error;
    }
  }

  /**
   * Get room status and participant information
   */
  async getRoomStatus(roomId: string, organizationId: string): Promise<RoomStatus> {
    try {
      const response = await fetch(`${REALTIMEKIT_CONFIG.baseUrl}/meetings/${roomId}`, {
        method: 'GET',
        headers: this.headers
      });

      if (!response.ok) {
        throw new Error(`Failed to get room status: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const meeting = data.data;

      // Verify this room belongs to the organization (security check)
      if (!this.verifyRoomOwnership(meeting.title, organizationId)) {
        throw new Error('Access denied: Room does not belong to organization');
      }

      // Transform RealtimeKit response to our API format
      const status: RoomStatus = {
        roomId: meeting.id,
        name: this.extractOriginalName(meeting.title),
        status: meeting.status?.toLowerCase() || 'unknown',
        participants: {
          current: meeting.participant_count || 0,
          max: meeting.max_participants || 10,
          list: meeting.participants?.map((p: any) => ({
            participantId: p.id,
            name: p.name,
            role: p.metadata?.role || 'participant',
            joinedAt: p.joined_at || new Date().toISOString(),
            status: p.status || 'connected'
          })) || []
        },
        duration: this.calculateDuration(meeting.created_at),
        recording: {
          enabled: meeting.metadata?.recordingEnabled || false,
          status: 'not_recording', // RealtimeKit doesn't provide this
          duration: 0
        }
      };

      return status;
    } catch (error) {
      console.error('[PlatformVideo] Error getting room status:', error);
      throw error;
    }
  }

  /**
   * End a meeting room
   */
  async endRoom(roomId: string, organizationId: string): Promise<{ success: boolean; endedAt: string }> {
    try {
      // First verify ownership
      const roomStatus = await this.getRoomStatus(roomId, organizationId);
      
      const response = await fetch(`${REALTIMEKIT_CONFIG.baseUrl}/meetings/${roomId}`, {
        method: 'PATCH',
        headers: this.headers,
        body: JSON.stringify({
          status: 'INACTIVE'
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to end room: ${response.status} ${response.statusText}`);
      }

      const endedAt = new Date().toISOString();

      // TODO: Log room ended for billing
      await this.logRoomEnded(organizationId, roomId, roomStatus.duration);

      return {
        success: true,
        endedAt
      };
    } catch (error) {
      console.error('[PlatformVideo] Error ending room:', error);
      throw error;
    }
  }

  /**
   * List rooms for an organization with filtering
   */
  async listRooms(
    organizationId: string,
    status?: 'active' | 'waiting' | 'ended',
    limit: number = 20,
    offset: number = 0
  ): Promise<{ rooms: PlatformRoom[]; total: number }> {
    try {
      // Get all meetings (RealtimeKit doesn't support filtering)
      const response = await fetch(`${REALTIMEKIT_CONFIG.baseUrl}/meetings`, {
        method: 'GET',
        headers: this.headers
      });

      if (!response.ok) {
        throw new Error(`Failed to list rooms: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const allMeetings = data.data || [];

      // Filter for this organization's platform rooms
      const orgPrefix = `${this.PLATFORM_PREFIX}_${organizationId}_`;
      let filteredRooms = allMeetings
        .filter((meeting: any) => meeting.title?.startsWith(orgPrefix))
        .map((meeting: any) => this.transformMeetingToRoom(meeting, organizationId));

      // Apply status filter if specified
      if (status) {
        filteredRooms = filteredRooms.filter((room: PlatformRoom) => room.status === status);
      }

      // Apply pagination
      const total = filteredRooms.length;
      const paginatedRooms = filteredRooms.slice(offset, offset + limit);

      return {
        rooms: paginatedRooms,
        total
      };
    } catch (error) {
      console.error('[PlatformVideo] Error listing rooms:', error);
      throw error;
    }
  }

  /**
   * Verify that a room belongs to the specified organization
   */
  private verifyRoomOwnership(roomTitle: string, organizationId: string): boolean {
    const expectedPrefix = `${this.PLATFORM_PREFIX}_${organizationId}_`;
    
    // Allow bypass for legal consultation template demo
    if (organizationId === 'legal_template_demo') {
      // For legal template demo, allow access to any room (bypass organization check)
      console.log('[PlatformVideo] Bypassing organization check for legal template demo');
      return true;
    }
    
    return roomTitle.startsWith(expectedPrefix);
  }

  /**
   * Extract original room name from namespaced name
   */
  private extractOriginalName(namespacedName: string): string {
    // Format: cc_platform_orgId_originalname_timestamp
    const parts = namespacedName.split('_');
    if (parts.length >= 4) {
      // Remove prefix, orgId, and timestamp, join the rest
      const originalParts = parts.slice(3, -1); // Remove prefix (cc_platform_orgId) and timestamp
      return originalParts.join('_').replace(/_/g, ' ');
    }
    return namespacedName;
  }

  /**
   * Calculate duration in seconds since creation
   */
  private calculateDuration(createdAt: string): number {
    return Math.floor((Date.now() - new Date(createdAt).getTime()) / 1000);
  }

  /**
   * Transform RealtimeKit meeting data to our room format
   */
  private transformMeetingToRoom(meeting: any, organizationId: string): PlatformRoom {
    return {
      roomId: meeting.id,
      name: this.extractOriginalName(meeting.title),
      namespacedName: meeting.title,
      maxParticipants: meeting.max_participants || 10,
      status: meeting.status?.toLowerCase() || 'waiting',
      createdAt: meeting.created_at,
      metadata: {
        customerId: organizationId, // Backward compatibility
        organizationId,
        originalName: this.extractOriginalName(meeting.title),
        purpose: meeting.metadata?.purpose
      }
    };
  }

  /**
   * Log room creation for usage tracking (TODO: implement D1 integration)
   */
  private async logRoomCreation(organizationId: string, room: PlatformRoom): Promise<void> {
    // TODO: Insert into D1 database platform_usage table
    console.log('[PlatformVideo] TODO: Log room creation for billing:', {
      organizationId,
      roomId: room.roomId,
      action: 'room_created',
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Log token generation for usage tracking (TODO: implement D1 integration)
   */
  private async logTokenGeneration(organizationId: string, roomId: string, token: PlatformToken): Promise<void> {
    // TODO: Insert into D1 database platform_usage table
    console.log('[PlatformVideo] TODO: Log token generation for billing:', {
      organizationId,
      roomId,
      participantId: token.participantId,
      action: 'token_generated',
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Log room ended for billing calculation (TODO: implement D1 integration)
   */
  private async logRoomEnded(organizationId: string, roomId: string, duration: number): Promise<void> {
    // TODO: Calculate participant minutes and insert into D1 database
    console.log('[PlatformVideo] TODO: Log room ended for billing:', {
      organizationId,
      roomId,
      duration,
      action: 'room_ended',
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Check if a room name belongs to the platform (utility method)
   */
  isPlatformRoom(roomName: string): boolean {
    return roomName.startsWith(this.PLATFORM_PREFIX);
  }

  /**
   * Extract organization ID from namespaced room name (utility method)
   */
  extractOrganizationId(roomName: string): string | null {
    if (!this.isPlatformRoom(roomName)) {
      return null;
    }

    const parts = roomName.split('_');
    // Format: cc_platform_orgId_...
    if (parts.length >= 3) {
      return parts[2];
    }

    return null;
  }
}

// Export singleton instance
export const platformVideoService = new PlatformVideoService();
export default platformVideoService;